import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  fromEvaluation,
  formatOfferAmount,
  offerBenefitRows,
  type CheckoutPromotionViewData,
  type OfferBenefitKind,
  type OfferBenefitView,
} from '../../features/offers/offerBenefits'
import {
  selectIsOfferLoading,
  selectOfferEvaluation,
} from '../../features/offers/offersSlice'
import type { PromotionFreeItem } from '../../features/offers/promotionEvaluation'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import LoadingAnimation from '../shared/LoadingAnimation'
import { RootState } from '../../app/store'
import offerDiscountIcon from '../../assets/offers/offer_discount.svg'
import offerCashbackIcon from '../../assets/offers/offer_cashback.svg'
import offerFreeProductsIcon from '../../assets/offers/offer_free_products.svg'
import chevronBackward from '../../assets/images/chevron_backward.svg'

/** Figma tokens — nodes 16186:1842 / 16186:2026 */
const OFFER_BG = '#EDF9F2'
const OFFER_GREEN = '#1A5C35'
const OFFER_GREEN_SOFT = '#C8EDD8'
const OFFER_PRODUCT_BG = '#C8EDD8'
const OFFER_DIVIDER = '#1A5C35'
const OFFER_TITLE = '#1E1E1E'
const OFFER_DESC = '#6B7280'

function iconForKind(kind: OfferBenefitKind): string {
  if (kind === 'cashback') return offerCashbackIcon
  if (kind === 'free') return offerFreeProductsIcon
  return offerDiscountIcon
}

function resolveLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  title?: string,
  titleKey?: string,
): string {
  if (title && title.trim().length > 0) return title
  if (titleKey) return t(titleKey)
  return ''
}

function resolveDescription(
  t: (key: string, options?: Record<string, unknown>) => string,
  benefit: OfferBenefitView,
): string | null {
  if (benefit.description && benefit.description.trim().length > 0) {
    return benefit.description
  }
  if (benefit.descriptionKey) {
    return t(benefit.descriptionKey, benefit.descriptionParams)
  }
  return null
}

function figmaTitle(
  t: (key: string) => string,
  benefit: OfferBenefitView,
): string {
  if (benefit.kind === 'set_item') return t('offers.benefitSetItemDiscount')
  if (benefit.showMinus || benefit.kind === 'discount') return t('offers.benefitDiscount')
  if (benefit.kind === 'cashback') return t('offers.benefitCashback')
  return resolveLabel(t, benefit.title, benefit.titleKey) || t('offers.benefitDiscount')
}

function figmaDescription(
  t: (key: string, options?: Record<string, unknown>) => string,
  benefit: OfferBenefitView,
): string | null {
  const direct = resolveDescription(t, benefit)
  if (direct) return direct

  const fallbackTitles = new Set([
    t('offers.gotDiscount'),
    t('offers.gotCashback'),
    t('offers.gotGift'),
    t('offers.benefitDiscount'),
    t('offers.benefitCashback'),
    t('offers.benefitFreeProducts'),
  ])

  const title = resolveLabel(t, benefit.title, benefit.titleKey)
  const heading = figmaTitle(t, benefit)
  if (title && title !== heading && !fallbackTitles.has(title)) {
    return title
  }

  if (benefit.kind === 'cashback' && benefit.amount != null && benefit.amount > 0) {
    return t('offers.cashbackCredited', {
      amount: formatOfferAmount(benefit.amount),
    })
  }

  return null
}

const OfferBenefitIcon: React.FC<{ kind: OfferBenefitKind; size?: number }> = ({
  kind,
  size = 24,
}) => (
  <img
    src={iconForKind(kind)}
    alt=""
    width={size}
    height={size}
    className="shrink-0"
    style={{ width: size, height: size }}
  />
)

const ValueBadge: React.FC<{ amount: number; showMinus: boolean }> = ({
  amount,
  showMinus,
}) => (
  <div
    className="flex items-center gap-1 px-2 py-1 rounded-lg shrink-0"
    style={{ backgroundColor: OFFER_GREEN }}
  >
    <CurrencyDisplay
      className="w-3 h-3 text-white"
      iconStyle={{ filter: 'brightness(0) invert(1)' }}
    />
    <span className="text-xs font-bold text-white">
      {showMinus ? '-' : ''}
      {formatOfferAmount(amount)}
    </span>
  </div>
)

const FreeProductCard: React.FC<{ item: PromotionFreeItem }> = ({ item }) => {
  const { t } = useTranslation()
  const title =
    item.name && item.name.length > 0
      ? item.quantity != null && item.quantity > 1
        ? `${item.name} ${item.quantity}x`
        : item.name
      : t('offers.gotGift')

  return (
    <div
      className="flex items-center gap-2 px-2 py-2 rounded-xl min-w-[168px] max-w-[200px]"
      style={{ backgroundColor: OFFER_PRODUCT_BG }}
    >
      <OfferBenefitIcon kind="free" size={18} />
      {item.image ? (
        <img
          src={item.image}
          alt=""
          className="w-9 h-9 rounded-lg object-cover shrink-0"
          onError={(e) => {
            ;(e.target as HTMLImageElement).style.display = 'none'
          }}
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <div
          className="text-xs font-semibold leading-tight line-clamp-2"
          style={{ color: OFFER_TITLE }}
        >
          {title}
        </div>
        {item.originalPrice != null && item.originalPrice > 0 ? (
          <div
            className="flex items-center gap-0.5 mt-1 text-[11px] line-through"
            style={{ color: OFFER_DESC }}
          >
            <span>{formatOfferAmount(item.originalPrice)}</span>
            <CurrencyDisplay className="w-2.5 h-2.5" />
          </div>
        ) : null}
      </div>
    </div>
  )
}

const BenefitRow: React.FC<{ benefit: OfferBenefitView }> = ({ benefit }) => {
  const { t } = useTranslation()
  const title = figmaTitle(t, benefit)
  const description = figmaDescription(t, benefit)

  return (
    <div className="flex items-start gap-2.5">
      <OfferBenefitIcon kind={benefit.kind} size={24} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold leading-tight" style={{ color: OFFER_TITLE }}>
          {title}
        </div>
        {description ? (
          <div className="text-xs mt-1 leading-snug" style={{ color: OFFER_DESC }}>
            {description}
          </div>
        ) : null}
      </div>
      {benefit.amount != null && benefit.amount > 0 ? (
        <ValueBadge amount={benefit.amount} showMinus={benefit.showMinus} />
      ) : null}
    </div>
  )
}

export interface OfferCardProps {
  /** Display-only data (e.g. order promotion or send-order evaluation). */
  display?: CheckoutPromotionViewData
  /** When true, card stays expanded and header is not collapsible. */
  forceExpanded?: boolean
  className?: string
}

export const OfferCardContent: React.FC<OfferCardProps> = ({
  display: displayProp,
  forceExpanded = false,
  className = 'shrink-0 rounded-xl overflow-hidden',
}) => {
  const { t } = useTranslation()
  const evaluation = useSelector(selectOfferEvaluation)
  const isLoading = useSelector(selectIsOfferLoading)
  const language = useSelector((state: RootState) => state.settings.settings.language)
  const isRTL = language === 'ar'
  const [expanded, setExpanded] = useState(true)

  const benefits = useMemo(() => {
    if (displayProp) return offerBenefitRows(displayProp)
    if (!evaluation.hasEligibleOffer) return []
    return offerBenefitRows(fromEvaluation(evaluation))
  }, [displayProp, evaluation])

  if (!displayProp && isLoading) {
    return (
      <div className="shrink-0 px-1 py-2 flex" dir={isRTL ? 'rtl' : 'ltr'}>
        <LoadingAnimation
          type="staggeredDotsWave"
          size="small"
          color="gray"
          className="h-2 [&>div]:gap-0.5 [&>div>div]:w-1 [&>div>div]:h-1"
        />
      </div>
    )
  }

  if (benefits.length === 0) return null

  const valueRows = benefits.filter((row) => row.products.length === 0)
  const products = benefits.flatMap((row) => row.products)
  const countLabel =
    benefits.length === 1
      ? t('offers.benefitCount', { count: benefits.length })
      : t('offers.benefitsCount', { count: benefits.length })
  const isExpanded = forceExpanded || expanded

  return (
    <div
      className={className}
      style={{
        backgroundColor: OFFER_BG,
        border: `1.5px dashed ${OFFER_GREEN}`,
        borderRadius: 12,
        padding: 16,
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {forceExpanded ? (
        <div className="w-full flex items-start gap-2">
          <span className="text-lg leading-none shrink-0" aria-hidden>
            🎉
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold leading-tight" style={{ color: OFFER_GREEN }}>
              {t('offers.savingMore')}
            </div>
          </div>
          <div
            className="px-2.5 py-1.5 rounded-full text-xs font-semibold shrink-0"
            style={{ backgroundColor: OFFER_GREEN_SOFT, color: OFFER_GREEN }}
          >
            {countLabel}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-start gap-2 text-start cursor-pointer"
        >
          <span className="text-lg leading-none shrink-0" aria-hidden>
            🎉
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold leading-tight" style={{ color: OFFER_GREEN }}>
              {t('offers.savingMore')}
            </div>
          </div>
          <div
            className="px-2.5 py-1.5 rounded-full text-xs font-semibold shrink-0"
            style={{ backgroundColor: OFFER_GREEN_SOFT, color: OFFER_GREEN }}
          >
            {countLabel}
          </div>
          <img
            src={chevronBackward}
            alt=""
            width={16}
            height={16}
            className="shrink-0 mt-0.5"
            style={{
              width: 16,
              height: 16,
              transform: isExpanded ? 'rotate(90deg)' : isRTL ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.15s ease',
            }}
          />
        </button>
      )}

      {isExpanded ? (
        <div className="flex flex-col gap-4 mt-4">
          <div
            className="w-full border-t border-dashed"
            style={{ borderColor: OFFER_DIVIDER }}
          />
          {valueRows.map((benefit, index) => (
            <BenefitRow key={`benefit-${index}`} benefit={benefit} />
          ))}
          {products.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              <div className="text-sm font-bold" style={{ color: OFFER_TITLE }}>
                {t('offers.benefitFreeProducts')}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {products.map((item, index) => (
                  <FreeProductCard
                    key={`${item.productId ?? item.id ?? item.name}-${index}`}
                    item={item}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

const OfferCard: React.FC<OfferCardProps> = (props) => <OfferCardContent {...props} />

export default OfferCard
